/**
 * useAuth hook — provides auth state and actions from localStorage + API
 */
import { useState, useCallback, useEffect } from 'react';
import api from '../utils/api';

const getStoredUser = () => {
  try {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  } catch { return null; }
};

export const useAuth = () => {
  const [user, setUser] = useState(getStoredUser);
  const [loading, setLoading] = useState(false);

  // Re-fetch the current user from /auth/me — keeps profile, role, and
  // membership status fresh (call after joining/cancelling VIP).
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    try {
      const res = await api.get('/auth/me');
      localStorage.setItem('user', JSON.stringify(res.data));
      setUser(res.data);
      return res.data;
    } catch {
      return null; /* interceptor handles 401 */
    }
  }, []);

  // Re-hydrate from /auth/me on mount if a token exists.
  useEffect(() => { refreshUser(); }, [refreshUser]);

  // Cross-tab sync: if another tab logs in/out, reflect it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'user') setUser(getStoredUser());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      localStorage.setItem('accessToken', res.data.accessToken);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (_) {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const register = useCallback(async (data) => {
    setLoading(true);
    try {
      const res = await api.post('/auth/register', data);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    loading,
    login,
    logout,
    register,
    refreshUser,
    isAdmin: user?.role === 'admin',
    isMember: !!user?.membership?.active,
  };
};
